import { memo, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Node, Space, View, ViewFilters } from "@melo/shared";
import { store, useTables } from "../lib/store";
import { keyOf } from "../lib/select";
import { moveNode, send } from "../lib/actions";
import { openItem } from "../lib/nav";
import { isSelecting, selectClick, useSelection } from "../lib/selection";
import { BulkBar } from "../components/BulkBar";
import { Card } from "../components/Card";
import { Avatar } from "../components/primitives";
import { StatusDot } from "../components/pickers";
import { columnsFor, newItemParent, useViewNodes, type Column } from "./common";
import { uploadFiles } from "../lib/upload";
import { can, canEditNode } from "../lib/access";

type Layout = Record<string, string[]>;

export function Board({ space, view, filters }: { space: Space; view: View; filters: ViewFilters }) {
  const t = useTables();
  const { nodes, kinds } = useViewNodes(t, space, view, filters);
  const { columns, columnOf } = useMemo(() => columnsFor(t, view, kinds, nodes), [t, view, kinds, nodes]);
  const base = useMemo(() => {
    const out: Layout = Object.fromEntries(columns.map((c) => [c.key, [] as string[]]));
    for (const n of nodes) out[columnOf(n)]?.push(n.id);
    return out;
  }, [columns, columnOf, nodes]);

  // While dragging, the board follows the pointer in local state; on drop one command is sent.
  const [drag, setDrag] = useState<{ id: string; from: string; layout: Layout } | null>(null);
  const layout = drag?.layout ?? base;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const containerOf = (id: string, l: Layout) => (id in l ? id : Object.keys(l).find((k) => l[k]!.includes(id)));

  const onStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    setDrag({ id, from: containerOf(id, base) ?? "", layout: base });
  };
  const onOver = (e: DragOverEvent) => {
    if (!drag || !e.over) return;
    const overId = String(e.over.id);
    const from = containerOf(drag.id, drag.layout);
    const to = containerOf(overId, drag.layout);
    if (!from || !to || from === to) return;
    const col = columns.find((c) => c.key === to);
    const node = store.t.nodes.get(drag.id);
    if (!col?.change || !node || !col.change(node)) return;
    const next = { ...drag.layout, [from]: drag.layout[from]!.filter((x) => x !== drag.id) };
    const target = [...drag.layout[to]!];
    const idx = overId in drag.layout ? target.length : Math.max(0, target.indexOf(overId));
    target.splice(idx, 0, drag.id);
    next[to] = target;
    setDrag({ ...drag, layout: next });
  };
  const onEnd = (e: DragEndEvent) => {
    const d = drag;
    setDrag(null);
    if (!d || !e.over) return;
    const node = store.t.nodes.get(d.id);
    if (!node) return;
    const to = containerOf(d.id, d.layout)!;
    let list = [...d.layout[to]!];
    const overId = String(e.over.id);
    if (!(overId in d.layout) && overId !== d.id) {
      const from = list.indexOf(d.id);
      const target = list.indexOf(overId);
      if (from >= 0 && target >= 0) {
        list.splice(from, 1);
        list.splice(target, 0, d.id);
      }
    }
    const i = list.indexOf(d.id);
    const col = columns.find((c) => c.key === to)!;
    const crossed = to !== d.from;
    const sameSpot = !crossed && base[to]!.indexOf(d.id) === i;
    if (sameSpot) return;
    const after = list[i - 1];
    const before = list[i + 1];
    const change = crossed ? (col.change?.(node) ?? null) : {};
    if (crossed && !change) return;
    void moveNode(node, { ...(after ? { afterId: after } : before ? { beforeId: before } : {}), ...change }, crossed ? `${keyOf(store.t, node)} moved to ${col.label}` : undefined);
  };

  const active = drag ? t.nodes.get(drag.id) : undefined;
  const showFields = view.config.showFields;
  const order = columns.flatMap((c) => layout[c.key] ?? []);
  return (
    <div className="board-wrap">
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onStart} onDragOver={onOver} onDragEnd={onEnd} onDragCancel={() => setDrag(null)}>
        <div className="board">
          {columns.map((c) => (
            <BoardColumn key={c.key} column={c} ids={layout[c.key] ?? []} space={space} view={view} kindKey={kinds.length === 1 ? kinds[0]!.key : (kinds.find((k) => k.key === "task")?.key ?? kinds[0]?.key)} showFields={showFields} dragging={drag?.id} order={order} />
          ))}
        </div>
        <DragOverlay dropAnimation={{ duration: 160, easing: "cubic-bezier(0.25, 0.4, 0.25, 1)" }}>{active ? <div className="card dragging-card"><Card node={active} showFields={showFields} /></div> : null}</DragOverlay>
      </DndContext>
      <BulkBar spaceId={space.id} kinds={kinds} />
    </div>
  );
}

const BoardColumn = memo(function BoardColumn({ column, ids, space, view, kindKey, showFields, dragging, order }: { column: Column; ids: string[]; space: Space; view: View; kindKey: string | undefined; showFields: string[]; dragging?: string; order: string[] }) {
  const t = useTables();
  const selection = useSelection();
  const { setNodeRef, isOver } = useDroppable({ id: column.key });
  const [fileOver, setFileOver] = useState(false);
  const actor = column.actorId ? t.actors.get(column.actorId) : undefined;
  const over = column.wip !== undefined && ids.length > column.wip;
  const canCreate = Boolean(column.change && kindKey && can(space.id, "member"));
  return (
    <section
      className={`col${isOver || fileOver ? " over" : ""}`}
      aria-label={column.label}
      // A picture dropped on a column (not on a card) becomes a new card with that picture as its cover.
      onDragOver={(e) => {
        if (!canCreate || !e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        setFileOver(true);
      }}
      onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as globalThis.Node | null) && setFileOver(false)}
      onDrop={(e) => {
        setFileOver(false);
        const files = [...e.dataTransfer.files];
        if (!canCreate || !files.length) return;
        e.preventDefault();
        void cardsFromFiles(files, space, view, column, kindKey!, ids.at(-1));
      }}
    >
      <header className="col-h">
        {column.status && <StatusDot status={column.status} />}
        {actor && <Avatar actor={actor} size="sm" />}
        <span className="col-title">{column.label}</span>
        <span className="n">{ids.length}</span>
        {column.hint && <span className="col-hint">{column.hint}</span>}
        {column.wip !== undefined && <span className={`lim${over ? " over" : ""}`} title="Work-in-progress limit">{ids.length}/{column.wip}</span>}
      </header>
      <SortableContext id={column.key} items={ids} strategy={verticalListSortingStrategy}>
        <div ref={setNodeRef} className="col-b">
          {ids.map((id) => {
            const n = t.nodes.get(id);
            return n ? <SortableCard key={id} node={n} showFields={showFields} hidden={dragging === id} selected={selection.has(id)} order={order} /> : null;
          })}
          {!ids.length && <div className="col-empty">Drop here</div>}
        </div>
      </SortableContext>
      {column.change && kindKey && <QuickAdd space={space} view={view} column={column} kindKey={kindKey} lastId={ids.at(-1)} />}
    </section>
  );
});

const SortableCard = memo(function SortableCard({ node, showFields, hidden, selected, order }: { node: Node; showFields: string[]; hidden: boolean; selected: boolean; order: string[] }) {
  const t = useTables();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: node.id });
  const [fileOver, setFileOver] = useState(false);
  const editable = canEditNode(node);
  return (
    <div
      ref={setNodeRef}
      className={`card${hidden ? " placeholder" : ""}${selected ? " selected" : ""}${fileOver ? " file-over" : ""}`}
      // Drop pictures or files on a card to attach them. The first picture becomes the cover.
      onDragOver={(e) => {
        if (!editable || !e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        e.stopPropagation();
        setFileOver(true);
      }}
      onDragLeave={() => setFileOver(false)}
      onDrop={(e) => {
        setFileOver(false);
        const files = [...e.dataTransfer.files];
        if (!editable || !files.length) return;
        e.preventDefault();
        e.stopPropagation();
        void uploadFiles(files, { spaceId: node.spaceId, nodeId: node.id });
      }}
      data-node={node.id}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...attributes}
      {...listeners}
      aria-roledescription="Draggable card"
      aria-label={`${keyOf(t, node)} ${node.title}`}
      onClick={(e) => {
        // With a selection going, or with a modifier key, a click selects instead of opening.
        if (e.metaKey || e.ctrlKey || e.shiftKey || isSelecting()) selectClick(node.id, order, e);
        else openItem(keyOf(store.t, node));
      }}
      onKeyDown={(e) => {
        listeners?.onKeyDown?.(e);
        if (e.key === "Enter" && !e.defaultPrevented) openItem(keyOf(store.t, node));
        if (e.key.toLowerCase() === "x" && !e.metaKey && !e.ctrlKey) selectClick(node.id, order, e);
      }}
    >
      <Card node={node} showFields={showFields} selected={selected} onSelect={(e) => selectClick(node.id, order, e)} />
    </div>
  );
});

/** "harbor_mood-v2.png" becomes "Harbor mood v2". */
function titleFromFile(name: string) {
  const base = name.replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ").trim();
  return base ? base[0]!.toUpperCase() + base.slice(1) : "Untitled";
}

async function cardsFromFiles(files: File[], space: Space, view: View, column: Column, kindKey: string, lastId?: string) {
  const kind = [...store.t.kinds.values()].find((k) => k.spaceId === space.id && k.key === kindKey);
  let after = lastId;
  for (const file of files) {
    const id = crypto.randomUUID();
    const draft = { title: file.name, kindId: kind?.id, status: kind?.statuses[0]?.key ?? "", fields: {}, assignees: [], labels: [], parentId: newItemParent(view) } as unknown as Node;
    const change = column.change?.(draft) ?? {};
    await send("node.create", {
      spaceId: space.id,
      nodes: [{ id, kind: kindKey, title: titleFromFile(file.name), parentId: change.parentId !== undefined ? change.parentId : newItemParent(view), status: change.status, fields: change.fields ?? undefined, assignees: change.assignees, labels: change.labels, ...(after ? { afterId: after } : {}) }],
    });
    after = id;
    await uploadFiles([file], { spaceId: space.id, nodeId: id });
  }
}

function QuickAdd({ space, view, column, kindKey, lastId }: { space: Space; view: View; column: Column; kindKey: string; lastId?: string }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const input = useRef<HTMLTextAreaElement>(null);
  const create = async () => {
    const text = title.trim();
    if (!text) return setOpen(false);
    setTitle("");
    const kind = [...store.t.kinds.values()].find((k) => k.spaceId === space.id && k.key === kindKey);
    const draft: Partial<Node> = { title: text, kindId: kind?.id, status: kind?.statuses[0]?.key ?? "", fields: {}, assignees: [], parentId: newItemParent(view) };
    const change = column.change?.(draft as Node) ?? {};
    await send("node.create", {
      spaceId: space.id,
      nodes: [
        {
          kind: kindKey,
          title: text,
          parentId: change.parentId !== undefined ? change.parentId : newItemParent(view),
          status: change.status,
          fields: change.fields ?? undefined,
          assignees: change.assignees,
          ...(lastId ? { afterId: lastId } : {}),
        },
      ],
    });
    input.current?.focus();
  };
  if (!open)
    return (
      <button type="button" className="col-add" onClick={() => setOpen(true)}>
        + Add
      </button>
    );
  return (
    <div className="quick-add">
      <textarea
        ref={input}
        autoFocus
        rows={2}
        className="textarea"
        placeholder="Title, then Enter"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => !title.trim() && setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void create();
          }
          if (e.key === "Escape") setOpen(false);
        }}
      />
    </div>
  );
}
